import { useEffect, useMemo } from 'react'
import Image from 'next/legacy/image'
import Link from 'next/link'
import { renderContentItems } from 'utilities/contentful'
import s from './styles.module.css'
import Categories from '../categories'
import Share from '../share'
import Section from '../section'
import Navbar from '../navbar'
import Footer from '../footer'
import * as D from 'date-fns'
import CardList from '../section/card-list'
import Card from '../section/card'
import { ArrowUpIcon } from 'components/design-system/kickoff-icons'
import { Post, TopNav } from '../../../types/blog-types'
import LastPosts from './last-posts'
import { Hidden } from 'components/design-system/hidden'
import { CTA } from '../cta'
import { StickyCTA } from '../experiments/sticky-cta'
import { useAnalytics } from 'hooks/use-analytics'

const mainCategory = post =>
  post.fields.categories?.filter(({ fields }) => !fields.parent)

interface PostProps {
  topNav?: TopNav
  post: Post
  relatedPosts?: [Post]
  latestAuthorPosts?: [Post]
  latestPosts?: [Post]
  footer: any
  isMobile: boolean
}

/**
 * Utility used to split a blog post content in half
 * so that we can display a CTA halfway through the post
 */
const useSplitBlogContent = (post: Post) => {
  const { firstHalfContent, secondHalfContent } = useMemo(() => {
    if (!post || !post.fields.content)
      return { firstHalfPosts: [], secondHalfPosts: [] }
    const content = post.fields.content.content

    const getNearestParagraphIndex = (index: number) => {
      const nextContent = content[index]
      if (nextContent.nodeType === 'paragraph') {
        return index
      } else {
        return getNearestParagraphIndex(index + 1)
      }
    }

    const middleIndex = getNearestParagraphIndex(
      Math.floor(content.length / 2) + 1
    )

    const firstHalfContent = content.slice(0, middleIndex)
    const secondHalfContent = content.slice(middleIndex)

    return { firstHalfContent, secondHalfContent }
  }, [post])

  return { firstHalfContent, secondHalfContent }
}

const PostCardList = ({ posts, title }: { posts: [Post]; title: string }) => {
  if (!posts?.length) return null

  return (
    <Section title={title} className={s.related}>
      <CardList className={s.relatedPosts}>
        {posts.map(post => {
          return (
            <Card
              key={`${title}-${post.sys.id}`}
              className={s.relatedPost}
              categories={mainCategory(post)}
              post={post}
            />
          )
        })}
      </CardList>
    </Section>
  )
}

export default function BlogPost({
  topNav,
  post,
  relatedPosts,
  footer,
  latestAuthorPosts,
  latestPosts,
  isMobile,
}: PostProps) {
  const logEvent = useAnalytics()

  useEffect(() => {
    if (post?.fields?.slug) {
      logEvent('View', {}, true)
    }
  }, [post?.fields?.slug])
  const handleBackToTopClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const { firstHalfContent, secondHalfContent } = useSplitBlogContent(post)

  // TODO: Fix this
  if (!post) return null

  const author = post.fields.author?.fields
  const latestPostsByAuthorTitle = `Latest Posts by ${
    author.name.split(' ')[0]
  }`

  return (
    <div>
      <Navbar template={topNav} />
      <main className={s.main}>
        <article className={s.article}>
          <header>
            <Categories
              categories={post.fields.categories}
              className={s.categories}
            />
            <h1 className={s.title}>{post.fields.title}</h1>
            {post.fields.subtitle && (
              <p className={s.subtitle}>{post.fields.subtitle}</p>
            )}
            <ul className={s.headerUl}>
              <li>
                <time dateTime={post.fields.date || post.sys.updatedAt}>
                  {D.format(
                    post.fields.date || post.sys.updatedAt,
                    'MMMM D, YYYY'
                  )}
                </time>
              </li>
              <li>
                <Link href={`author/${author.slug}`} className={s.link}>
                  {author.name}
                </Link>
              </li>
              {/* <li>
                <span>10 min read</span>
              </li> */}
            </ul>
            {post.fields.experts && post.fields.experts && (
              <div className={s.expertsWrapper}>
                <span className={s.label}>Expert Reviewed:</span>
                <span className={s.experts}>
                  {post.fields.experts.map(({ sys, fields }) => (
                    <Link
                      key={sys.id}
                      href={`expert/${fields.slug}`}
                      className={s.link}>
                      {fields.name}
                    </Link>
                  ))}
                </span>
              </div>
            )}
            <hr className={s.hr} />
            {post.fields.editorialIntegrity && (
              <div className={s.editorialIntegrity}>
                {renderContentItems(
                  post.fields.editorialIntegrity.fields.content.content
                )}
              </div>
            )}
            <hr className={s.hr} />
            <Share
              id="mobile-share"
              title={post.fields.title}
              slug={post.fields.slug}
              className={s.share}
            />
            {post.fields.featureImage && (
              <figure>
                <div className={s.featureImage}>
                  <Image
                    alt={post.fields.featureImage.fields.title}
                    src={`https:${post.fields.featureImage.fields.file.url}`}
                    layout="fill"
                    objectFit="cover"
                  />
                </div>
                <figcaption>
                  {post.fields.featureImage.fields.description ||
                    post.fields.featureImage.fields.title}
                </figcaption>
              </figure>
            )}
          </header>
          <div className={s.content}>
            {renderContentItems(firstHalfContent)}
            <CTA category={post?.fields?.categories?.[0]?.fields?.slug} />
            {renderContentItems(secondHalfContent)}
          </div>
        </article>
        <aside className={s.aside}>
          <div className={s.coachBioAside}>
            {!!author?.profileImage?.fields && (
              <div className={s.authorImage}>
                <Image
                  alt={author.profileImage.fields.title}
                  src={`https:${author.profileImage.fields.file.url}`}
                  width={48}
                  height={48}
                  objectFit="cover"
                />
              </div>
            )}
            <Link href={`author/${author.slug}`} className={s.authorName}>
              {author.name}
            </Link>
            {author.bio && <span className={s.authorDesc}>{author.bio}</span>}
            <hr className={s.hr} />
            <Share
              id="desktop-share"
              title={post.fields.title}
              slug={post.fields.slug}
              className={s.share}
            />
            <hr className={s.hr} />
            <button
              className={s.scrollToTopButton}
              onClick={handleBackToTopClick}>
              <ArrowUpIcon className="" />
              Back to top
            </button>
            <Hidden visible={!!latestAuthorPosts.length}>
              <hr className={s.hr} />
              <LastPosts
                className={s.share}
                latestAuthorPosts={latestAuthorPosts}
                title={latestPostsByAuthorTitle}
              />
            </Hidden>
          </div>
        </aside>
      </main>
      <Hidden visible={isMobile && !!latestAuthorPosts.length}>
        <PostCardList
          title={latestPostsByAuthorTitle}
          posts={latestAuthorPosts}
        />
      </Hidden>
      <PostCardList title="Related" posts={relatedPosts} />
      <PostCardList title="Latest" posts={latestPosts} />

      <StickyCTA text="Free Training for 7 Days" />
      <Footer items={footer} />
    </div>
  )
}
